import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { ui } from "../config/theme";
import { PlusCircle, Search, SortAsc, Trash2 } from "lucide-react";
import { API_BASE_URL } from "../config/apiconfig";

const CareerAdvice = () => {
    const [articles, setArticles] = useState([]);
    const [search, setSearch] = useState("");
    const [sortBy, setSortBy] = useState("title-asc");
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();
    const { user } = useAuth();

    useEffect(() => {
        const fetchArticles = async () => {
            try {
                setLoading(true);
                const response = await fetch(`${API_BASE_URL}/api/articles`);
                const data = await response.json();
                setArticles(data);
            } catch (error) {
                console.error("Error fetching articles:", error);
            } finally {
                setLoading(false);
            }
        };
        fetchArticles();
    }, []);

    const handleDelete = async (id) => {
        if (!window.confirm("Delete this article? This cannot be undone.")) return;
        try {
            const res = await fetch(`${API_BASE_URL}/api/articles/${id}`, {
                method: "DELETE",
            });
            if (!res.ok) throw new Error("Failed to delete article");
            setArticles((prev) => prev.filter((a) => a.id !== id));
        } catch (err) {
            console.error(err);
            alert("Could not delete article. Please try again.");
        }
    };

    const filtered = articles.filter((a) => {
        const q = search.trim().toLowerCase();
        if (!q) return true;
        return (
            (a.title || "").toLowerCase().includes(q) ||
            (a.description || "").toLowerCase().includes(q)
        );
    });

    const sorted = [...filtered].sort((a, b) => {
        const [field, dir] = sortBy.split("-");
        if (field === "title") {
            const av = (a.title || "").toLowerCase();
            const bv = (b.title || "").toLowerCase();
            if (av < bv) return dir === "asc" ? -1 : 1;
            if (av > bv) return dir === "asc" ? 1 : -1;
            return 0;
        }
        if (field === "date") {
            const ad = new Date(a.createdAt || 0).getTime();
            const bd = new Date(b.createdAt || 0).getTime();
            return dir === "asc" ? ad - bd : bd - ad;
        }
        return 0;
    });

    return (
        <div className={ui.page}>
            <Header />
            <main className="flex-grow">
                <div className={ui.container}>
                    <div className={`${ui.sectionCard} p-8`}>
                        <div className="flex justify-between items-center mb-6">
                            <h1 className="text-3xl font-semibold text-gray-900">Career Advice</h1>
                            {(user?.role === 'e2e-admin' || user?.role === 'recruiter') && (
                                <button
                                    onClick={() => navigate("/add-career-advice")}
                                    className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium transition-colors"
                                >
                                    <PlusCircle className="w-5 h-5" />
                                    <span className="hidden sm:inline">Add Article</span>
                                </button>
                            )}
                        </div>

                        <div className="flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between mb-8">
                            <div className="relative w-full sm:max-w-md">
                                <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                                <input
                                    type="text"
                                    value={search}
                                    onChange={(e) => setSearch(e.target.value)}
                                    placeholder="Search by title or description"
                                    className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                />
                            </div>
                            <div className="flex items-center gap-2">
                                <SortAsc className="w-4 h-4 text-gray-500" />
                                <select
                                    value={sortBy}
                                    onChange={(e) => setSortBy(e.target.value)}
                                    className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                >
                                    <option value="title-asc">Title A-Z</option>
                                    <option value="title-desc">Title Z-A</option>
                                    <option value="date-desc">Newest</option>
                                    <option value="date-asc">Oldest</option>
                                </select>
                            </div>
                        </div>

                        {loading && (
                            <div className="text-gray-500 mb-4">Loading articles...</div>
                        )}

                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                            {sorted.map((article) => (
                                <div
                                    key={article.id}
                                    className="bg-white p-6 rounded-xl shadow-md hover:shadow-lg transition-all duration-300"
                                >
                                    <h2 className="text-xl font-semibold text-gray-900 mb-3">
                                        {article.title}
                                    </h2>
                                    <p className="text-gray-600 text-sm mb-4">
                                        {article.description}
                                    </p>
                                    <Link
                                        to={`/articles/${article.id}`}
                                        className="inline-flex items-center text-blue-600 font-medium hover:text-blue-700 transition-colors"
                                    >
                                        Read More
                                        <span className="ml-1">→</span>
                                    </Link>
                                    {(user?.role === 'e2e-admin' || user?.role === 'recruiter') && (
                                        <div className="mt-4">
                                            <button
                                                onClick={() => handleDelete(article.id)}
                                                className="inline-flex items-center gap-2 text-red-600 hover:text-red-700"
                                                title="Delete article"
                                            >
                                                <Trash2 className="w-4 h-4" /> Delete
                                            </button>
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </main>
            <Footer />
        </div>
    );
};

export default CareerAdvice;